import type { INonsoleReadinessService } from '../../services/nonsoleReadiness/INonsoleReadinessService';
import { isNonsoleReadinessServiceError } from '../../services/nonsoleReadiness/nonsoleReadinessErrors';
import type { NonsoleReadinessReport } from '@playcastdotio/NonsoleReadinessTypes';
import { categorizeGates, type CategorizedGates } from './categorizeGates';
import { serviceContainer } from '../../services/ServiceContainer';
import {
  formatHostModeSoftFailMessage,
  hostModeErrorCodeFromGates,
  hostModeErrorCodeFromNativeError,
  hostModeErrorCodeFromThrow,
  reportHostModeFailure,
  type HostModeErrorCode,
} from './hostModeErrors';

export type HostModeEntryPhase =
  | 'idle'
  | 'checking'
  | 'apps-running'
  | 'restart-setup'
  | 'please-wait'
  | 'contact-support'
  | 'several-issues'
  | 'restart-confirm';

const PLEASE_WAIT_POLL_MS = 5_000;

export interface HostModeEntryControllerDeps {
  nonsoleReadinessService: INonsoleReadinessService;
  /**
   * Whether the shell should offer Host Mode at all, from the stored tier.
   *
   * This is the OFFER, not enforcement: the service gate remains the authority
   * once Phase 6 is installed. A missing detail means the machine has not been
   * evaluated, and the shell must not offer entry in that state.
   */
  getEntryOffer?: () => { offer: boolean; reason: string };
}

export class HostModeEntryController {
  private phase: HostModeEntryPhase = 'idle';
  private categorized: CategorizedGates | null = null;
  private error: string | null = null;
  private failureCode: HostModeErrorCode | null = null;
  private inFlight = false;
  private generation = 0;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private listeners: Array<() => void> = [];
  private destroyed = false;

  private readonly service: INonsoleReadinessService;
  private readonly deps: HostModeEntryControllerDeps;
  private startedAt = 0;

  constructor(deps: HostModeEntryControllerDeps) {
    this.deps = deps;
    this.service = deps.nonsoleReadinessService;
  }

  getPhase(): HostModeEntryPhase { return this.phase; }
  getCategorized(): CategorizedGates | null { return this.categorized; }
  getError(): string | null { return this.error; }
  getFailureCode(): HostModeErrorCode | null { return this.failureCode; }
  isInFlight(): boolean { return this.inFlight; }

  /**
   * Whether the shell should render the Activate Host Mode control.
   *
   * An explicit stored-tier decision wins. Otherwise this is permissive (the
   * controller never blocks on its own) — the service gate is the authority, and
   * hiding a button is not enforcement.
   */
  isEntryOffered(): boolean {
    const offer = this.deps.getEntryOffer?.();
    return offer ? offer.offer : true;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      const idx = this.listeners.indexOf(listener);
      if (idx > -1) this.listeners.splice(idx, 1);
    };
  }

  async start(): Promise<void> {
    if (this.inFlight || this.destroyed) return;

    this.generation++;
    const gen = this.generation;

    this.inFlight = true;
    this.phase = 'checking';
    this.error = null;
    this.failureCode = null;
    this.startedAt = Date.now();
    serviceContainer.analyticsManager.sendHostModeActivationStartedEvent();
    this.notify();

    let report: NonsoleReadinessReport;
    try {
      report = await this.service.getFreshReport();
    } catch (err) {
      if (this.destroyed || gen !== this.generation) return;
      this.handleSoftFailure(err, 'checking');
      return;
    }

    if (this.destroyed || gen !== this.generation) return;
    await this.route(report, gen);
  }

  async retry(): Promise<void> {
    if (this.inFlight || this.destroyed) return;
    await this.start();
  }

  async recheck(): Promise<void> {
    if (this.inFlight || this.destroyed) return;
    const gen = this.generation;

    this.inFlight = true;
    // Do NOT reset phase — keeps the current modal stable during poll
    this.notify();

    let report: NonsoleReadinessReport;
    try {
      report = await this.service.getFreshReport();
    } catch {
      if (this.destroyed || gen !== this.generation) return;
      this.inFlight = false;
      this.notify();
      return;
    }

    if (this.destroyed || gen !== this.generation) return;
    await this.route(report, gen);
  }

  confirmRestart(): void {
    if (this.destroyed || this.phase !== 'restart-setup') return;
    this.phase = 'restart-confirm';
    this.notify();
  }

  dismiss(): void {
    if (this.destroyed) return;
    this.generation++;
    this.clearPollTimer();
    this.inFlight = false;
    this.phase = 'idle';
    this.categorized = null;
    this.error = null;
    this.failureCode = null;
    this.notify();
  }

  destroy(): void {
    this.destroyed = true;
    this.clearPollTimer();
    this.listeners = [];
  }

  private static readonly MAX_ROUTE_DEPTH = 3;

  private async route(report: NonsoleReadinessReport, gen: number, depth = 0): Promise<void> {
    if (depth >= HostModeEntryController.MAX_ROUTE_DEPTH) {
      this.inFlight = false;
      this.phase = 'contact-support';
      this.categorized = {
        appsHolding: [],
        userActions: [],
        pleaseWait: [],
        contactSupport: [{
          gateId: 'ENTRY_LOOP',
          category: 'contact-support',
          label: "Can't start Host Mode",
          detail: 'Repeated entry attempts failed. Please try again or contact support.',
        }],
        clearToEnter: false,
      };
      this.failureCode = 'HM_ENTRY_LOOP';
      reportHostModeFailure({
        code: 'HM_ENTRY_LOOP',
        phase: 'entry-loop',
        gateIds: ['ENTRY_LOOP'],
        durationMs: Date.now() - this.startedAt,
      });
      serviceContainer.analyticsManager.sendHostModeActivationFailedEvent({
        error: 'HM_ENTRY_LOOP',
        phase: 'entry-loop',
        errorCode: 'HM_ENTRY_LOOP',
      });
      this.notify();
      return;
    }

    this.clearPollTimer();
    const cats = categorizeGates(report);
    this.categorized = cats;

    if (cats.clearToEnter) {
      try {
        const result = await this.service.requestEntry();
        if (this.destroyed || gen !== this.generation) return;

        if (result.accepted) {
          this.inFlight = false;
          this.phase = 'idle';
          this.categorized = null;
          this.failureCode = null;
          serviceContainer.analyticsManager.sendHostModeActivationSucceededEvent({
            totalDurationMs: Date.now() - this.startedAt,
          });
          this.notify();
          return;
        }

        if (result.report) {
          await this.route(result.report, gen, depth + 1);
          return;
        }

        const nativeError = result.error ?? 'unknown';
        const code = hostModeErrorCodeFromNativeError(nativeError);
        this.inFlight = false;
        this.phase = 'contact-support';
        this.failureCode = code;
        this.categorized = {
          appsHolding: [],
          userActions: [],
          pleaseWait: [],
          contactSupport: [{
            gateId: 'UNKNOWN',
            category: 'contact-support',
            label: "Can't start Host Mode",
            detail: nativeError,
          }],
          clearToEnter: false,
        };
        reportHostModeFailure({
          code,
          phase: 'entry-rejected',
          gateIds: ['UNKNOWN'],
          nativeError,
          durationMs: Date.now() - this.startedAt,
        });
        serviceContainer.analyticsManager.sendHostModeActivationFailedEvent({
          error: code,
          phase: 'entry-rejected',
          errorCode: code,
        });
        this.notify();
        return;
      } catch (err) {
        if (this.destroyed || gen !== this.generation) return;
        this.handleSoftFailure(err, 'entry-request');
        return;
      }
    }

    this.inFlight = false;
    this.phase = this.resolvePhase(cats);
    if (this.isHardStopPhase(this.phase, cats)) {
      this.recordHardStopFromGates(this.phase, cats);
    } else {
      this.failureCode = null;
    }
    this.notify();

    if (this.phase !== 'idle') {
      const failingGates = [
        ...cats.appsHolding,
        ...cats.userActions.map(g => g.gateId),
        ...cats.pleaseWait.map(g => g.gateId),
        ...cats.contactSupport.map(g => g.gateId),
      ];
      serviceContainer.analyticsManager.sendHostModeActivationBlockedEvent({
        phase: this.phase,
        failingGates,
      });
    }

    // A wait condition can coexist with another visible issue and therefore
    // resolve to the "several issues" modal. Keep polling whenever any wait
    // gate is present so an open modal is replaced as readiness changes.
    if (cats.pleaseWait.length > 0) {
      this.startPollTimer();
    }
  }

  private handleSoftFailure(err: unknown, phase: string): void {
    const code = hostModeErrorCodeFromThrow(err);
    this.inFlight = false;
    this.failureCode = code;
    this.error = formatHostModeSoftFailMessage(code);
    this.phase = 'idle';
    this.categorized = null;
    reportHostModeFailure({
      code,
      phase,
      nativeError: isNonsoleReadinessServiceError(err) ? err.nativeError : undefined,
      durationMs: Date.now() - this.startedAt,
    });
    serviceContainer.analyticsManager.sendHostModeActivationFailedEvent({
      error: code,
      phase,
      errorCode: code,
    });
    this.notify();
  }

  private isHardStopPhase(phase: HostModeEntryPhase, cats: CategorizedGates): boolean {
    if (phase === 'contact-support') {
      return true;
    }
    if (phase === 'several-issues' && cats.contactSupport.length > 0) {
      return true;
    }
    return false;
  }

  private recordHardStopFromGates(phase: string, cats: CategorizedGates): void {
    const gateIds = cats.contactSupport.map((g) => g.gateId);
    const code = hostModeErrorCodeFromGates(cats.contactSupport);
    this.failureCode = code;
    reportHostModeFailure({
      code,
      phase,
      gateIds,
      durationMs: Date.now() - this.startedAt,
    });
  }

  private resolvePhase(cats: CategorizedGates): HostModeEntryPhase {
    const groups: Array<'appsHolding' | 'userActions' | 'pleaseWait' | 'contactSupport'> = [];
    if (cats.appsHolding.length > 0) groups.push('appsHolding');
    if (cats.userActions.length > 0) groups.push('userActions');
    if (cats.pleaseWait.length > 0) groups.push('pleaseWait');
    if (cats.contactSupport.length > 0) groups.push('contactSupport');

    if (groups.length === 1) {
      const single = groups[0];
      if (single === 'appsHolding') return 'apps-running';
      if (single === 'userActions') {
        if (cats.userActions.length === 1 && cats.userActions[0].actionKind === 'reboot') {
          return 'restart-setup';
        }
        return 'several-issues';
      }
      if (single === 'pleaseWait') return 'please-wait';
      if (single === 'contactSupport') return 'contact-support';
    }

    return 'several-issues';
  }

  private startPollTimer(): void {
    this.clearPollTimer();
    this.pollTimer = setInterval(() => {
      this.recheck();
    }, PLEASE_WAIT_POLL_MS);
  }

  private clearPollTimer(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
